/* A trip's GPS trail, whole, when it no longer fits on one document (bug 216).
 *
 * From 2026-09-25 the driver app spills the oldest breadcrumbs into
 * `{trip}/trail/{seq}` chunk documents once the live array passes ~2,000
 * points, and keeps a recent window in `routePoints`. A screen that draws a
 * whole run goes through loadTripTrailRaw: nothing extra for an unspilled
 * trip, one subcollection read for a spilled one. Points come back exactly as
 * stored — sealed or plain — for the caller to open.
 *
 * Same rule as services/tracking/trip_trail.dart on the phone and
 * engine/route/trail.js on the backend. */
import { collection, doc, getDocs, orderBy, query, type Firestore } from "firebase/firestore";

export type TrailChunk = { seq?: number; count?: number; points?: unknown[] };

export function assembleTrail(chunks: TrailChunk[], window: unknown[], windowStart?: number | null): unknown[] {
  const ordered = [...chunks].sort((a, b) => Number(a.seq ?? 0) - Number(b.seq ?? 0));
  const full: unknown[] = [];
  for (const c of ordered) if (Array.isArray(c.points)) full.push(...c.points);
  if (!full.length) return window.slice();
  const spilled = full.length;
  const start = typeof windowStart === "number" && Number.isFinite(windowStart) ? windowStart : spilled;
  // A window copied before the last spill starts inside the chunks: trim the
  // overlap. One that starts after them is a gap, appended as-is.
  const skip = start < spilled ? spilled - start : 0;
  return full.concat(skip >= window.length ? [] : window.slice(skip));
}

export type TrailBearing = {
  routePoints?: unknown[];
  trailChunks?: number | null;
  trailWindowStart?: number | null;
};

export async function loadTripTrailRaw(
  db: Firestore,
  tripCollection: string,
  tripId: string,
  data: TrailBearing,
): Promise<unknown[]> {
  const window = Array.isArray(data.routePoints) ? data.routePoints : [];
  const chunkCount = Number(data.trailChunks ?? 0);
  if (!(chunkCount > 0)) return window;
  try {
    const snap = await getDocs(query(collection(doc(db, tripCollection, tripId), "trail"), orderBy("seq")));
    return assembleTrail(snap.docs.map((c) => c.data() as TrailChunk), window, data.trailWindowStart ?? null);
  } catch {
    // A subcollection we cannot read is a shorter trail, not a broken one.
    return window;
  }
}
